<template>
  <div class="col-sm-12 nopads">
    <div class="search-form">
      <div class="col-sm-12 search-form-title" style="margin-left: .5em">
        <font-awesome-icon icon="search" />{{ $t('common.search') }}
      </div>
      <form
        id="delivery-note-search"
        class="mt-2 p-2"
        @submit.prevent="onSubmit"
        @reset="onReset"
      >
        <div class="row">
          <div class="col-sm-4">
            <open-contract-filter
              :from="fromDate"
              :to="toDate"
              :contract="contract"
              :apply-padding="false"
              :row-view="false"
              :show-only-my-contracts-option="true"
              :show-title="true"
              :show-company="false"
              :storage-contract-ids="storageContractIds"
              @contract-selected="setContract"
            />
              <div>
                  <input
                      type="checkbox"
                      class="form-check-input mx-3"
                      style="margin-right: .5em"
                      v-model="onlyUserOwnedContracts"
                      id="onlyUserOwnedContracts"
                  />
                  <label class="form-check-label" for="onlyUserOwnedContracts" style="font-size: .9em">
                      {{ $t('contracts.only_user_owned_contracts') }}
                  </label>
              </div>
          </div>
          <div class="col-sm-4">
              <contract-order-multiselect
                  ref="orderFilter"
                  :contract-ids="contract ?  [contract.id] : []"
                  :single-mode="true"
                  @orders-selected="setOrder"
              />
          </div>
          <div v-if="materials && materials.length > 0" class="col-sm-4" style="padding-top: .1em">
            <span class="search-title">{{ $t('delivery_note.material') }}</span>
            <select v-model="material" class="form-select form-select-sm" :disabled="!materials || !!loading">
                  <option :value="null">
                      {{ $t('delivery_note.form.select_material') }}
                  </option>
                  <option v-for="material in materials" :key="material.id" :value="material.id">
                      {{ material.material.name + ' (' + material.type_test_code + ')' }}
                  </option>
            </select>
          </div>
        </div>
        <div class="row px-3">
          <div class="col-sm-4">
            <span class="search-title">{{ $t('delivery_note.date') }} - {{
              $t('trip_list.road_sec_from')
            }}</span>
            <date-time-picker
              v-model:date="fromDate"
              class="routa-input"
              format="dd.MM.yyyy HH:mm"
              :enableTimePicker="true"
              :placeholder="$t('observations_videos.search_input_format')"
            />
          </div>
          <div class="col-sm-4">
            <span class="search-title">{{ $t('delivery_note.date') }} - {{
              $t('trip_list.road_sec_to')
            }}</span>
            <date-time-picker
              v-model:date="toDate"
              class="routa-input"
              format="dd.MM.yyyy HH:mm"
              :enableTimePicker="true"
              :placeholder="$t('observations_videos.search_input_format')"
            />
          </div>
        </div>
        <div class="row px-3" style="overflow: visible;">
          <div class="col-sm-4 pb-4">
            <span class="search-title">{{ $t('trip_list.vehicle') }}</span>
            <multiselect
              v-model="vehicle"
              class="multiselect_routa"
              :placeholder="$t('trip_list.select_vehicle')"
              :deselect-label="$t('common.remove_selection')"
              :select-label="$t('common.select')"
              :close-on-select="true"
              label="name"
              track-by="name"
              maxHeight="200"
              :searchable="true"
              :internal-search="true"
              :selected-label="$t('common.selected')"
              :options="vehicleOptions"
            >
              <template
                #singleLabel="props"
              >
                <span class="option__desc">
                  <span class="option__title">{{ props.option.name }}</span>
                </span>
              </template>
              <template
                #option="props"
              >
                <div class="option__desc">
                  <span class="option__title multiselect_detail_block">{{
                    props.option.name
                  }}</span>
                </div>
              </template>
              <template #noResult>
                <span>{{ $t('common.not_found') }}</span>
              </template>
              <template #noOptions>
                <span>{{ $t('common.no_results') }}</span>
              </template>
            </multiselect>
          </div>
          <div
            v-if="showStorages"
            class="col-sm-4"
          >
            <material-storage-filter
              v-model="storage"
              :search-title-style="true"
              @storage-selected="onStorageSelected"
            />
          </div>
          <div
            v-if="showStorages"
            class="col-sm-4"
          >
              <div class="form-check pt-4">
                  <input
                      type="checkbox"
                      class="form-check-input"
                      style="margin-right: .5em"
                      id="checkbox-1"
                      v-model="includeTrips"
                  />
                  <label class="form-check-label" for="checkbox-1">
                      {{ $t('delivery_note.include_trips') }}
                  </label>
              </div>
          </div>
        </div>
        <div class="col-sm-12 button-container">
            <button
                type="reset"
                class="btn btn-danger form-button"
            >
                {{ $t('delivery_note.clear') }}
            </button>
            <button
                type="submit"
                class="btn btn-primary form-button"
            >
                {{ $t('common.search') }}
            </button>
        </div>
      </form>
    </div>
    <div
      v-if="!!loading"
      id="loader"
      class="spinner"
    />
  </div>
</template>

<script>
import {restApi} from "@/components/mixins/RestApiMixin";
import {timeUtils} from "@/components/mixins/TimeUtils";
import {orderMixin} from "@/components/mixins/OrderMixin";
import MaterialStorageFilter from "@/components/materialstorage/MaterialStorageFilter";
import OpenContractFilter from "@/components/contract/OpenContractFilter";
import DateTimePicker from "@/components/common/DateTimePicker";
import ContractOrderMultiselect from "../order/ContractOrderMultiselect.vue";

export default {
    name: "DeliveryNoteSearch",
    components: {ContractOrderMultiselect, OpenContractFilter, MaterialStorageFilter, DateTimePicker},
    mixins: [restApi, timeUtils, orderMixin],
    props: {
        station: {
            type: Number
        },
        showStorages: {
            type: Boolean,
            default: false
        },
        contracts: {
            type: Array,
            default() {
                return []
            }
        },
        loginTime: {
            type: Date,
            default: null
        },
        materials: {
            type: Array,
            default() {
                return []
            }
        },
        showOnlyMyContractsOption: {
            type: Boolean,
            default: false
        }
    },
    data: function () {
        return {
            fromDate: this.loginTime ? this.loginTime.toISOString() : null,
            toDate: null,
            contract: null,
            order: null,
            material: null,
            loading: 0,
            vehicleOptions: [],
            vehicle: null,
            storage: [],
            storageContracts: null,
            includeTrips: false,
            onlyUserOwnedContracts: 0
        }
    },
    computed: {
        storageContractIds() {
            return this.storageContracts
        }
    },
    watch: {
        station: function () {
            this.selectStation()
        },
        onlyUserOwnedContracts: function() {
            this.$emit('onlyUserOwnedContractSelected', this.onlyUserOwnedContracts, "search")
        },
        contract: function () {
            if (!this.storageContracts) {
                this.storage = []
            }
            if (!this.order) {
                this.fetchVehicles()
            }
        },
        order: function () {
            this.fetchVehicles()
        },
        fromDate: function () {
            this.fetchVehicles()
        },
        toDate: function () {
            this.fetchVehicles()
        }
    },
    mounted() {
        if (!this.fromDate) {
            this.fromDate = this.getMonthStart()
        } else {
            this.fetchVehicles()
        }
    },
    methods: {
        setOrder(order) {
            this.order = order
        },
        getOptionSearchParams() {
            return {
                from: this.fromDate,
                to: this.toDate,
                contract: this.contract?.id,
                orders: this.order ? [this.order.id] : null,
                companies: this.companyParam ? this.companyParam.map(cParam => cParam.value) : null
            }
        },

        fetchVehicles: function () {
            this.loading++
            this.vehicleOptions = []
            let params = this.getOptionSearchParams()
            this.restFetchParams(this.tripVehicleUrl, params, this.handleVehicles)
        },

        /**
         * Builds the display label for a Vehicle_Option from a Vehicle_Record.
         * Appends the owning company's name in parentheses only when the vehicle
         * belongs to a subcontractor and a non-empty company name is present.
         * @param {Object} item - Vehicle_Record (make, vehicle_model, license_plate, subcontractor, company)
         * @returns {string} Vehicle_Label
         */
        buildVehicleLabel(item) {
            const baseLabel = item.make + ' ' + item.vehicle_model + ' - ' + item.license_plate
            const companyName = item.company && item.company.name ? item.company.name : ''
            if (companyName !== '') {
                return baseLabel + ' (' + companyName + ')'
            }
            return baseLabel
        },

        handleVehicles: function (response) {
            if (response && response.data) {
                this.vehicleOptions = response.data.map(item => ({
                    value: item.id,
                    name: this.buildVehicleLabel(item)
                }))
            }
            this.vehiclesLoaded = true
            this.loading--
        },

        setContract: function (contract) {
            this.contract = contract ? contract : null
        },

        onStorageSelected: function (storages) {
            if (storages && storages.length) {
                const contractIds = new Set()
                storages.forEach(s => (s.contracts || []).forEach(c => contractIds.add(c.id)))
                this.storageContracts = Array.from(contractIds)
            } else {
                this.storageContracts = null
            }
        },

        refreshParams: function() {
            this.onSubmit()
        },

        onSubmit: function () {
            let params = {}
            if (this.fromDate !== null && this.fromDate.trim()) {
                params.timeFrom = new Date(this.fromDate).getTime()
            }
            if (this.toDate !== null && this.toDate.trim()) {
                params.timeTo = new Date(this.toDate).getTime()
            }
            if (this.contract !== null) {
                params.contract = this.contract.id
            }
            if (this.order) {
                params.order = this.order.id
            }
            if (this.material !== null) {
                params.material = this.material
            }
            if (this.vehicle !== null) {
                params.vehicle = this.vehicle.value
            }
            if (this.storage && this.storage.length) {
                params.storage = this.storage
            }
            if (this.includeTrips) {
                params.includeTrips = 1
            }
            this.$emit('search', params)
        },
        onReset: function () {
            this.orders = []
            this.fromDate = null
            this.toDate = null
            this.contract = null
            this.vehicle = null
            this.storage = []
            this.storageContracts = null
            this.onlyUserOwnedContracts = 0
            this.order = null
            this.includeTrips = false
            this.material = null
            this.storage = null
            this.$refs.orderFilter.reset()
        },
        selectStation: function () {
            this.contract = null
            this.order = null
            this.material = null
            this.onlyUserOwnedContracts = 0
        }
    }
}
</script>
